import { useState, useCallback, useRef } from 'react';
import { AxiosError } from 'axios';
import { generateTaskDescriptionSuggestionStreaming, submitAIFeedback, AIIntent } from '../api/ai.api';
import { AITaskSuggestionOptions, AITaskSuggestionResponse, AIFeedbackType } from '../types/AI';

interface UseAITaskSuggestionOptions {
  workspaceId: number;
  onSuccess?: (response: AITaskSuggestionResponse & { intent?: AIIntent }) => void;
  onError?: (error: string) => void;
  onDescriptionChunk?: (chunk: string) => void;
  onChatChunk?: (chunk: string) => void;
  onIntent?: (intent: AIIntent, reasoning: string) => void;
  onChatDone?: () => void;
}

interface UseAITaskSuggestionReturn {
  loading: boolean;
  error: string | null;
  suggestion: string | null;
  chatResponse: string | null;
  intent: AIIntent | null;
  intentReasoning: string | null;
  contentId: number | null;
  cached: boolean;
  tokensUsed: number;
  cost: number;
  generateSuggestion: (
    taskTitle: string,
    taskType: string,
    currentDescription?: string,
    options?: AITaskSuggestionOptions
  ) => Promise<void>;
  submitFeedback: (feedback: AIFeedbackType, finalContent?: string) => Promise<void>;
  cancelGeneration: () => void;
  reset: () => void;
}

export const useAITaskSuggestion = ({
  workspaceId,
  onSuccess,
  onError,
  onDescriptionChunk,
  onChatChunk,
  onIntent,
  onChatDone,
}: UseAITaskSuggestionOptions): UseAITaskSuggestionReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [chatResponse, setChatResponse] = useState<string | null>(null);
  const [intent, setIntent] = useState<AIIntent | null>(null);
  const [intentReasoning, setIntentReasoning] = useState<string | null>(null);
  const [contentId, setContentId] = useState<number | null>(null);
  const [cached, setCached] = useState(false);
  const [tokensUsed, setTokensUsed] = useState(0);
  const [cost, setCost] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const generateSuggestion = useCallback(
    async (taskTitle: string, taskType: string, currentDescription?: string, options?: AITaskSuggestionOptions) => {
      setLoading(true);
      setError(null);
      setSuggestion('');
      setChatResponse('');
      setIntent(null);
      setIntentReasoning(null);

      // Create abort controller for cancellation
      const controller = new AbortController();
      abortControllerRef.current = controller;

      let fullDescription = '';
      let fullChat = '';
      let classifiedIntent: AIIntent | null = null;

      try {
        await generateTaskDescriptionSuggestionStreaming(
          {
            workspaceId,
            taskTitle,
            taskType,
            currentDescription,
            options,
          },
          {
            onIntent: (detectedIntent: AIIntent, reasoning: string) => {
              classifiedIntent = detectedIntent;
              setIntent(detectedIntent);
              setIntentReasoning(reasoning);
              onIntent?.(detectedIntent, reasoning);
            },
            onChatChunk: (chunk: string) => {
              fullChat += chunk;
              setChatResponse(fullChat);
              onChatChunk?.(chunk);
            },
            onDescriptionChunk: (chunk: string) => {
              fullDescription += chunk;
              setSuggestion(fullDescription);
              onDescriptionChunk?.(chunk);
            },
            onChatDone: () => {
              onChatDone?.();
            },
            onComplete: (metadata) => {
              setContentId(metadata.contentId);
              setCached(metadata.cached);
              setTokensUsed(metadata.tokensUsed);
              setCost(metadata.cost);

              onSuccess?.({
                suggestion: fullDescription,
                contentId: metadata.contentId,
                cached: metadata.cached,
                tokensUsed: metadata.tokensUsed,
                cost: metadata.cost,
                intent: metadata.intent || classifiedIntent || undefined,
              });
            },
            onError: (errorMsg: string) => {
              setError(errorMsg);
              onError?.(errorMsg);
            },
          },
          controller.signal
        );
      } catch (err: any) {
        if (err.name === 'AbortError') {
          console.log('AI generation cancelled');
          setError('Generation cancelled');
        } else {
          const errorMessage = err instanceof Error ? err.message : 'Failed to generate AI suggestion';
          setError(errorMessage);
          onError?.(errorMessage);
        }
      } finally {
        setLoading(false);
        abortControllerRef.current = null;
      }
    },
    [workspaceId, onSuccess, onError, onDescriptionChunk, onChatChunk, onIntent, onChatDone]
  );

  const cancelGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const submitFeedback = useCallback(
    async (feedback: AIFeedbackType, finalContent?: string) => {
      if (!contentId) {
        console.warn('No contentId available for feedback submission');
        return;
      }

      try {
        await submitAIFeedback({
          contentId,
          feedback,
          finalContent,
        });
      } catch (err) {
        console.error('Failed to submit AI feedback:', err);
        // Don't throw error to user - feedback is optional
      }
    },
    [contentId]
  );

  const reset = useCallback(() => {
    // Cancel any ongoing generation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setLoading(false);
    setError(null);
    setSuggestion(null);
    setChatResponse(null);
    setIntent(null);
    setIntentReasoning(null);
    setContentId(null);
    setCached(false);
    setTokensUsed(0);
    setCost(0);
  }, []);

  return {
    loading,
    error,
    suggestion,
    chatResponse,
    intent,
    intentReasoning,
    contentId,
    cached,
    tokensUsed,
    cost,
    generateSuggestion,
    submitFeedback,
    cancelGeneration,
    reset,
  };
};
